/**
 * Líder usando localStorage con lease/heartbeat/fencing
 * Fallback confiable cuando Web Locks no está disponible
 */

import { idleConfig } from '../config';
import { getBus } from '../bus';
import { getActivityDetector } from '../activity';
import { LogoutCallback, LockState } from '../types';
import { LOG_PREFIX, MESSAGE_TYPES, LOGOUT_REASONS, TIMEOUTS } from '../constants';

export class StorageLeader {
  private isRunning = false;
  private isLeader = false;
  private leaderId: string;
  private fencingToken = 0;
  private heartbeatIntervalId: number | null = null;
  private timeoutId: number | null = null;
  private unsubscribeBus: (() => void) | null = null;
  private logout: LogoutCallback;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private logoutScheduledAt: number | null = null;

  constructor(logout: LogoutCallback) {
    this.logout = logout;
    this.leaderId = this.generateLeaderId();
  }

  async start(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;

    // Suscribirse a mensajes
    this.setupMessageListener();

    // Intentar adquirir liderazgo
    await this.tryAcquireLeadership();

    // Si no somos líderes, monitorear para failover
    if (!this.isLeader) {
      this.startFailoverMonitoring();
    }
  }

  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    // Liberar liderazgo si lo tenemos
    if (this.isLeader) {
      this.releaseLeadership();
    }

    // Limpiar intervalos y timeouts
    this.clearHeartbeat();
    this.clearTimeout();
    this.clearCountdown();

    // Desuscribirse del bus
    if (this.unsubscribeBus) {
      this.unsubscribeBus();
      this.unsubscribeBus = null;
    }
  }

  private generateLeaderId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupMessageListener() {
    const bus = getBus();
    this.unsubscribeBus = bus.subscribe((message) => {
      // Solo procesar mensajes si somos líderes
      if (!this.isLeader) return;

      // Ignorar mensajes con fencing token menor
      if (message.fencingToken && message.fencingToken < this.fencingToken) {
        return;
      }

      if (message.type === MESSAGE_TYPES.ACTIVITY) {
        this.onActivityReceived(message.ts);
      }
    });
  }

  private async tryAcquireLeadership(): Promise<void> {
    try {
      const lockState = this.readLockState();
      const now = Date.now();

      // Verificar si el lock ha expirado
      if (!lockState || now > lockState.leaseExpiresAt) {

        await this.acquireLeadership();
      } else {
        throw new Error('Lock not available');
      }
    } catch (error) {
      // Reintentar después de un delay
      setTimeout(() => {
        if (this.isRunning) {
          this.tryAcquireLeadership();
        }
      }, TIMEOUTS.LEADER_ELECTION_DELAY_MS);

      console.warn(`${LOG_PREFIX} Error trying to acquire leadership:`, error);
    }
  }

  private async acquireLeadership(): Promise<void> {
    const now = Date.now();
    this.fencingToken = now; // Usar timestamp como fencing token

    const newLockState: LockState = {
      leaderId: this.leaderId,
      leaseExpiresAt: now + idleConfig.leaseMs,
      fencingToken: this.fencingToken,
      acquiredAt: now,
    };

    try {
      this.writeLockState(newLockState);
      this.isLeader = true;

      // Iniciar heartbeat
      this.startHeartbeat();

      // Programar timeout inicial
      this.scheduleTimeout();
    } catch (error) {
      this.isLeader = false;
      console.warn(`${LOG_PREFIX} Error acquiring leadership:`, error);
    }
  }

  private releaseLeadership(): void {
    if (!this.isLeader) return;

    try {
      localStorage.removeItem(idleConfig.storageKeys.lock);
      localStorage.removeItem(idleConfig.storageKeys.leaderHeartbeat);
    } catch (error) {
      console.warn(`${LOG_PREFIX} Error releasing leadership:`, error);
    }

    this.isLeader = false;
    this.clearHeartbeat();
    this.clearTimeout();
    this.clearCountdown();
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();

    this.heartbeatIntervalId = window.setInterval(() => {
      if (!this.isLeader || !this.isRunning) {
        this.clearHeartbeat();
        return;
      }

      try {
        const now = Date.now();
        
        // Actualizar lease
        const lockState = this.readLockState();
        if (lockState && lockState.leaderId === this.leaderId) {
          lockState.leaseExpiresAt = now + idleConfig.leaseMs;
          this.writeLockState(lockState);
        }

        localStorage.setItem(idleConfig.storageKeys.leaderHeartbeat, now.toString());
      } catch (error) {
        console.warn(`${LOG_PREFIX} Error in heartbeat:`, error);
        // Si falla el heartbeat, liberar liderazgo
        this.releaseLeadership();
      }
    }, idleConfig.heartbeatMs);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
  }

  private startFailoverMonitoring(): void {
    const checkFailover = () => {
      if (!this.isRunning || this.isLeader) return;

      const lockState = this.readLockState();
      const now = Date.now();

      // Si el lock ha expirado, intentar adquirir liderazgo
      if (!lockState || now > lockState.leaseExpiresAt) {
        this.tryAcquireLeadership();
        return;
      }

      // Continuar monitoreando
      setTimeout(checkFailover, idleConfig.heartbeatMs);
    };

    setTimeout(checkFailover, idleConfig.heartbeatMs);
  }

  private onActivityReceived(activityTimestamp: number): void {
    if (!this.isLeader || !this.isRunning) return;

    // Reprogramar timeout con nueva actividad
    this.scheduleTimeout(activityTimestamp);
  }

  private scheduleTimeout(lastActivity?: number): void {
    this.clearTimeout();

    if (!this.isLeader || !this.isRunning) return;

    // Obtener última actividad
    const activityDetector = getActivityDetector();
    const lastActivityTime = lastActivity || activityDetector.getLastActivity();
    
    // Calcular tiempo restante
    const now = Date.now();
    const elapsed = now - lastActivityTime;
    const remaining = idleConfig.idleMs - elapsed;

    if (remaining <= 0) {
      this.executeLogout();
      return;
    }

    this.timeoutId = window.setTimeout(() => {
      this.executeLogout();
    }, remaining);

    // Iniciar cuenta atrás visual
    this.startCountdown(remaining);
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private clearCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
      this.logoutScheduledAt = null;
    }
  }

  private startCountdown(remainingMs: number) {
    this.clearCountdown();

    this.logoutScheduledAt = Date.now() + remainingMs;

    // Countdown cada segundo
    this.countdownInterval = setInterval(() => {
      if (!this.logoutScheduledAt || !this.isRunning) {
        this.clearCountdown();
        return;
      }

      const now = Date.now();
      const remaining = this.logoutScheduledAt - now;

      if (remaining <= 0) {
        this.clearCountdown();
        return;
      } 
    }, 1000);
  }

  private executeLogout(): void {
    if (!this.isLeader || !this.isRunning) return;

    // Notificar a otras pestañas
    try {
      const bus = getBus();
      bus.publish({
        type: MESSAGE_TYPES.LOGOUT,
        fencingToken: this.fencingToken,
        reason: LOGOUT_REASONS.IDLE_TIMEOUT,
      });
    } catch (error) {
      console.warn(`${LOG_PREFIX} Error notifying logout:`, error);
    }

    // Ejecutar logout local
    try {
      this.logout(LOGOUT_REASONS.IDLE_TIMEOUT);
    } catch (error) {
      console.error(`${LOG_PREFIX} Error executing logout:`, error);
    }
  }

  private readLockState(): LockState | null {
    try {
      const value = localStorage.getItem(idleConfig.storageKeys.lock);
      const lockState = value ? JSON.parse(value) : null;

      return lockState;
    } catch (error) {
      console.warn(`${LOG_PREFIX} Error reading lock state:`, error);
      return null;
    }
  }

  private writeLockState(lockState: LockState): void {
    try {
      const serialized = JSON.stringify(lockState);

      localStorage.setItem(idleConfig.storageKeys.lock, serialized);
    } catch (error) {
      console.error(`${LOG_PREFIX} Error writing lock state:`, error);
      throw error;
    }
  }
}
